/* RESPONSIBLE TEAM: team-reporting */

import type {
  Cell,
  ChartConfig,
  ChartDataResponse,
  GroupedData,
  RenderContext,
  TimeInterval,
} from '@/features/reporting-charts/types';
import { isDimensionCell, isDualQueryCell, isGroupedData } from '@/features/reporting-charts/types';
import {
  findPeriod,
  formatRoundedValue,
  formatValue,
} from '@/features/reporting-charts/transforms/utils';
import {
  CSAT_COLOR_MAPPINGS,
  CSAT_METRICS,
  DEFAULT_HEATMAP_COLORS,
} from '@/features/reporting-charts/constants';

type HeatmapPoint = Highcharts.Point & { value?: number | null };

interface HeatmapDataPoint {
  x: number;
  y: number;
  value: number | null;
}

function formatDayOfWeek(value: number, locale: string): string {
  if (value < 1 || value > 7) return String(value);
  return new Date(Date.UTC(1970, 0, value + 4)).toLocaleDateString(locale, {
    weekday: 'short',
    timeZone: 'UTC',
  });
}

export function buildHeatmapOptions(
  data: ChartDataResponse,
  config: ChartConfig,
  renderContext: RenderContext,
): Highcharts.Options {
  const current = findPeriod(data, 'current');

  if (!current || !isGroupedData(current.data) || current.data.length === 0) {
    return emptyOptions();
  }

  const metricMetadata = current.metadata.metricMetadata;
  const primaryUnit = metricMetadata[0]?.unit ?? 'value';
  const primaryMetricId = config.chartSeries?.[0]?.metricId ?? metricMetadata[0]?.metricId;

  const { xCategories, yCategories, seriesData } = buildHeatmapData(
    current.data,
    config.viewByTimeInterval,
    config.segmentByTimeInterval,
    renderContext.locale,
  );

  // Determine color scheme based on metric type
  const colorStops =
    primaryMetricId && CSAT_METRICS.has(primaryMetricId)
      ? CSAT_COLOR_MAPPINGS
      : DEFAULT_HEATMAP_COLORS;

  // Rounded single-unit format for compact data labels (matches Ember heatmap cells)
  const formatLabelValue = (value: number | null) =>
    formatRoundedValue(value, primaryUnit, renderContext);
  // Full multi-unit format for tooltips and color axis labels
  const formatMetricValue = (value: number | null) =>
    formatValue(value, primaryUnit, renderContext);

  return {
    chart: {
      type: 'heatmap',
      height: null,
      marginTop: 20,
      marginBottom: 100,
      plotBorderWidth: 0,
    },
    xAxis: {
      categories: xCategories,
      title: { text: undefined },
    },
    yAxis: {
      categories: yCategories,
      reversed: true,
      title: { text: undefined },
    },
    colorAxis: {
      stops: colorStops,
      min: 0,
      tickPositioner: function (this: Highcharts.Axis) {
        return [this.min ?? 0, this.max ?? 100] as number[];
      },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return formatMetricValue(typeof this.value === 'number' ? this.value : null);
        },
      },
    },
    series: [
      {
        type: 'heatmap',
        data: seriesData,
        dataLabels: {
          enabled: true,
          formatter: function (this: HeatmapPoint) {
            return formatLabelValue(this.value ?? null);
          },
          style: {
            color: 'var(--text-default)',
            textOutline: 'none',
          },
        },
      } as Highcharts.SeriesOptionsType,
    ],
    tooltip: {
      useHTML: true,
      outside: true,
      shared: false,
      backgroundColor: 'var(--neutral-fill)',
      borderColor: 'var(--neutral-border)',
      borderRadius: 10,
      style: {
        color: 'var(--text-on-neutral)',
      },
      formatter: function (this: HeatmapPoint) {
        const formattedVal = formatMetricValue(this.value ?? null);
        const yIdx = typeof this.y === 'number' ? this.y : 0;
        const xIdx = typeof this.x === 'number' ? this.x : 0;
        const yCat = yCategories[yIdx] ?? '';
        const xCatRaw = xCategories[xIdx] ?? '';
        const xCat = config.segmentByTimeInterval === 'hour_of_day' ? `${xCatRaw}:00` : xCatRaw;
        return `<strong>${formattedVal}</strong><br/>(${yCat} at ${xCat})`;
      },
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      y: 20,
      symbolHeight: 25,
    },
    credits: { enabled: false },
  };
}

function formatDimensionValue(
  rawValue: string | number,
  displayValue: string,
  interval: TimeInterval | undefined,
  locale: string,
): string {
  // For day_of_week, convert numeric value (1-7) to localized weekday name
  if (interval === 'day_of_week' && typeof rawValue === 'number') {
    return formatDayOfWeek(rawValue, locale);
  }

  // For hour_of_day, just use the raw numeric value (0-23)
  if (interval === 'hour_of_day' && typeof rawValue === 'number') {
    return String(rawValue);
  }

  // Otherwise use the displayValue from the data
  return displayValue;
}

function getSortKey(rawValue: string | number): number {
  if (typeof rawValue === 'number') {
    return rawValue;
  }
  const parsed = parseFloat(rawValue);
  return isNaN(parsed) ? 0 : parsed;
}

interface CategoryMeta {
  displayValue: string;
  sortKey: number;
}

function addDimensionCategory(
  categoryMap: Map<string, CategoryMeta>,
  cell: Cell | undefined,
  interval: TimeInterval | undefined,
  locale: string,
): void {
  if (cell === undefined || !isDimensionCell(cell)) {
    return;
  }

  const key = String(cell.rawValue);
  if (categoryMap.has(key)) {
    return;
  }

  categoryMap.set(key, {
    displayValue: formatDimensionValue(cell.rawValue, cell.displayValue, interval, locale),
    sortKey: getSortKey(cell.rawValue),
  });
}

function finalizeCategories(categoryMap: Map<string, CategoryMeta>): {
  categories: string[];
  indexMap: Map<string, number>;
} {
  const sortedEntries = Array.from(categoryMap.entries()).sort(
    (a, b) => a[1].sortKey - b[1].sortKey,
  );

  return {
    categories: sortedEntries.map(([, v]) => v.displayValue),
    indexMap: new Map(sortedEntries.map(([key], index) => [key, index])),
  };
}

/**
 * Transforms ChartDataResponse into Highcharts options for heatmap charts.
 *
 * Heatmaps require both viewBy and segmentBy dimensions to create a 2D grid:
 * - Y-axis = viewBy dimension (e.g., day of week)
 * - X-axis = segmentBy dimension (e.g., hour of day)
 * - Cell value = metric value
 *
 * Data shape: [[viewBy_dim, segmentBy_dim, val], ...]
 */
function buildHeatmapData(
  rows: GroupedData,
  viewByInterval: TimeInterval | undefined,
  segmentByInterval: TimeInterval | undefined,
  locale: string,
): {
  xCategories: string[];
  yCategories: string[];
  seriesData: HeatmapDataPoint[];
} {
  // Maps to track unique categories: rawValue -> { displayValue, sortKey }
  // Y-axis = viewBy (first dimension)
  // X-axis = segmentBy (second dimension)
  const yCategoryMap = new Map<string, CategoryMeta>();
  const xCategoryMap = new Map<string, CategoryMeta>();

  for (const [yCell, xCell] of rows) {
    addDimensionCategory(yCategoryMap, yCell, viewByInterval, locale);
    addDimensionCategory(xCategoryMap, xCell, segmentByInterval, locale);
  }

  const { categories: yCategories, indexMap: yIndexMap } = finalizeCategories(yCategoryMap);
  const { categories: xCategories, indexMap: xIndexMap } = finalizeCategories(xCategoryMap);

  const seriesData: HeatmapDataPoint[] = [];

  for (const [yCell, xCell, valueCell] of rows) {
    if (!isDimensionCell(yCell) || !isDimensionCell(xCell)) {
      continue;
    }

    const yIndex = yIndexMap.get(String(yCell.rawValue));
    const xIndex = xIndexMap.get(String(xCell.rawValue));

    if (yIndex === undefined || xIndex === undefined) {
      continue;
    }

    seriesData.push({
      x: xIndex,
      y: yIndex,
      value: extractNumericValue(valueCell),
    });
  }

  return { xCategories, yCategories, seriesData };
}

function extractNumericValue(cell: Cell | undefined): number | null {
  if (cell === null || cell === undefined) {
    return null;
  }

  if (typeof cell === 'number') {
    return cell;
  }

  if (isDualQueryCell(cell)) {
    return cell.value;
  }

  return null;
}

function emptyOptions(): Highcharts.Options {
  return {
    chart: { type: 'heatmap' },
    xAxis: { categories: [] },
    yAxis: { categories: [], reversed: true },
    colorAxis: {
      stops: DEFAULT_HEATMAP_COLORS,
    },
    series: [],
    legend: { enabled: false },
    credits: { enabled: false },
  };
}
